// Pure helpers shared by AI Earth, My Tundra, and the Phase 1 Agent job map.
// `setMapFindings` -> rows for the Map Findings list (jobState.mapFindings).
// `markerJson` -> regulated-entity tooltip markers (jobState.toolTipPoint, rendered by map/index.tsx).

const hasPointCoordinates = (geometry: any) =>
  Boolean(
    geometry
    && Array.isArray(geometry.coordinates)
    && geometry.coordinates.length >= 2
    && geometry.coordinates[0] != null
    && geometry.coordinates[1] != null
  );

/** True when a GeoJSON FeatureCollection has at least one feature with geometry. */
export const hasFeatureCollectionWithGeometry = (collection: any) =>
  Array.isArray(collection?.features)
  && collection.features.length > 0
  && collection.features.some((feature: any) => feature?.geometry != null);

/** Regulated-entity markers for EPR / map tooltips; never throws on bad input. */
export const getRegulatedEntities = (jobState: any) => {
  try {
    const esaFeatures = jobState?.esaData?.regulated_entities_point?.features;
    if (Array.isArray(esaFeatures) && esaFeatures.length > 0) {
      const fromEsa = markerJson(esaFeatures);
      if (fromEsa.length > 0) {
        return fromEsa;
      }
    }
    const toolTipPoint = jobState?.toolTipPoint;
    if (Array.isArray(toolTipPoint)) {
      return toolTipPoint.filter((entry: any) => entry?.entityNumber);
    }
  } catch {
    // ignore malformed job/ESA payloads
  }
  return [];
};

export const setMapFindings = (entPoint: any) => {
  if (!Array.isArray(entPoint) || entPoint.length === 0) {
    return '';
  }

  let jsonArray: any = '';
  for (const val of entPoint) {
    if (!val?.properties) {
      continue;
    }
    if (jsonArray) {
      jsonArray = jsonArray.concat([val.properties]);
    } else {
      jsonArray = [val.properties];
    }
  }
  return jsonArray;
};

export const markerJson = (fetchData: any) => {
  if (!Array.isArray(fetchData) || fetchData.length === 0) {
    return [];
  }

  const epnArray: any[] = [];
  for (const val of fetchData) {
    if (!val?.properties?.entity_number || !hasPointCoordinates(val.geometry)) {
      continue;
    }

    const epnRecord: any = {};
    epnRecord.location = Object.assign({});
    epnRecord.location.lon = val.geometry.coordinates[0];
    epnRecord.location.lat = val.geometry.coordinates[1];
    epnRecord.marker = 'STANDARD';
    epnRecord.name = val.properties.entity_name;
    epnRecord.type = 'ENTITY';
    epnRecord.details = val.properties;
    epnRecord.entityNumber = val.properties.entity_number;
    epnArray.push(epnRecord);
  }
  return epnArray;
};

export const SUMMARY_FETCH_TYPES = new Set([
  'ASTM Summary',
  'VEC Summary',
  'Oil-Gas Summary',
  'Orphans Summary',
  'Database Description',
  'Soils Summary',
]);

export const getFindingsSummaryFlags = (findings: any) => ({
  hasAstmSummaryData:
    Array.isArray(findings?.map_findings_summary_astm)
    && findings.map_findings_summary_astm.length > 0,
  hasVecSummaryData:
    Array.isArray(findings?.map_findings_summary_vec)
    && findings.map_findings_summary_vec.length > 0,
  hasOilGasSummaryData: Boolean(
    findings?.map_findings_summary_oil_gas
    && Array.isArray(findings.map_findings_summary_oil_gas.summary)
    && findings.map_findings_summary_oil_gas.summary.length > 0
  ),
  hasOrphansData:
    findings?.orphans != null && Object.keys(findings.orphans).length > 0,
  hasDatabaseDescriptionData:
    findings?.database_description?.programs != null
    && Object.keys(findings.database_description.programs).length > 0,
  hasSoilsSummaryData:
    Array.isArray(findings?.map_findings_summary_soils)
    && findings.map_findings_summary_soils.length > 0,
});
